import { Box, Stack, Text, HStack, Icon } from "@chakra-ui/react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  FileText,
  BookOpen,
  Inbox,
  History,
  MapPin,
  BarChart3,
  Building2,
  Users,
  ClipboardCheck,
  SlidersHorizontal,
  HardHat,
  AlertTriangle,
} from "lucide-react";
import { colors } from "@/theme/colors";
import { useAuth } from "@/context/AuthContext";

// Nav items filtered by the user's role AND the two-tier feature gate
// (company entitlement + role access). `feature` marks optional features.
const items = [
  { label: "Dashboard", icon: LayoutDashboard, to: "/", roles: ["super_admin", "org_admin", "supervisor", "field_employee"] },
  { label: "Companies & Users", icon: Building2, to: "/platform", roles: ["super_admin"] },
  { label: "Form Templates", icon: ClipboardList, to: "/templates", roles: ["super_admin"] },
  { label: "Field Forms", icon: FileText, to: "/forms", roles: ["org_admin", "supervisor", "field_employee"] },
  { label: "Resources", icon: BookOpen, to: "/resources", roles: ["super_admin", "org_admin", "supervisor", "field_employee"], feature: "safety_bulletins" },
  { label: "Urgent Items", icon: AlertTriangle, to: "/urgent", roles: ["org_admin", "supervisor"] },
  { label: "Submissions", icon: Inbox, to: "/submissions", roles: ["super_admin", "org_admin", "supervisor"] },
  { label: "My Tasks", icon: ClipboardCheck, to: "/tasks", roles: ["field_employee"] },
  { label: "My Sites & Crew", icon: Users, to: "/my-crew", roles: ["supervisor"] },
  { label: "Locations & Employees", icon: MapPin, to: "/setup", roles: ["org_admin"] },
  { label: "Contractors", icon: HardHat, to: "/contractors", roles: ["org_admin"], feature: "contractors" },
  { label: "Analytics", icon: BarChart3, to: "/analytics", roles: ["super_admin", "org_admin"], feature: "analytics" },
  { label: "Activity", icon: History, to: "/activity", roles: ["super_admin", "org_admin"] },
  { label: "Company Settings", icon: SlidersHorizontal, to: "/settings", roles: ["org_admin"] },
];

export const NavItems = ({ onNavigate, role }) => {
  const { pathname } = useLocation();
  const { hasFeature } = useAuth();
  const visible = items.filter((it) => role && it.roles.includes(role) && (!it.feature || hasFeature(it.feature)));

  return (
    <Stack gap="1" px="3" py="2">
      <Text px="3" pb="2" fontSize="xs" fontWeight="700" letterSpacing="0.08em" textTransform="uppercase" color={colors.textSecondary}>
        Workspace
      </Text>

      {visible.map(({ label, icon, to }) => {
        const active = pathname === to;
        return (
          <Box
            as={Link}
            to={to}
            key={to}
            onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
            display="block"
            borderRadius="10px"
            textDecoration="none"
            transition="background-color 0.15s ease, color 0.15s ease"
            bg={active ? colors.tealTint : "transparent"}
            _hover={{ bg: active ? colors.tealTint : colors.cream }}
          >
            <HStack gap="3" px="3" py="2.5" position="relative">
              {active && (
                <Box position="absolute" left="0" top="50%" transform="translateY(-50%)" w="3px" h="60%" borderRadius="full" bg={colors.teal} />
              )}
              <Icon as={icon} boxSize="5" color={active ? colors.teal : colors.textSecondary} />
              <Text fontSize="sm" fontWeight={active ? "600" : "500"} color={active ? colors.tealHover : colors.textPrimary}>
                {label}
              </Text>
            </HStack>
          </Box>
        );
      })}
    </Stack>
  );
};
